import { DiscordSDK, Events } from '@discord/embedded-app-sdk';

const CLIENT_ID = process.env.PUBLIC_DISCORD_CLIENT_ID!;
let QUEST_ID = '';
let DISCORD_LOCALE_FOR_ANALYTICS = '';

import * as ariseDiscord from './arise-discord-api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { IS_DISCORD_ACTIVITY } from '@/globals';
import * as arise from '@/arise/api';
import store, { useAppDispatch, useAppSelector } from '@/state/store';
import {
    setQuestStatus,
    setWindowSize,
    setDiscordLocale,
    setDiscordUser,
    setQuestFromSDK,
} from '@/state/features/discord';
import { updateIsLoggedIn } from '@/state/features/auth';
import { resetProfileState, setProfileV2DisplayName } from '@/state/features/profile';
import { setShouldHideUI } from '@/state/features/app';
import { getTokenPayload } from '@/utils/auth';
import { setUserId } from '@/utils/analytics/internal';
import { sendEventV2, sendEventV2Once } from '@/utils/analytics';
import { useTheme } from '@/theme';
import { useLang } from '@/components/TranslatedText';
import { OAuthScopes } from '@discord/embedded-app-sdk/output/schema/types';
import { getSavedLangIso } from '@/utils/localLang';
import { syncDiscordUserIdentity } from '@/utils/localIdentity';

const url = new URL(window.location.href);
const ARISE_SESSION_ID = url.searchParams.get('arise_session_id');
const ARISE_SESSION_DATA = url.searchParams.get('arise_session_data');
const activityStartTime = Date.now();

// remove the session parameters from the URL and update the URL without reloading the page
if (ARISE_SESSION_ID) {
    console.log('[Arise] Removing session ID from URL');
    url.searchParams.delete('arise_session_id');
}
if (ARISE_SESSION_DATA) {
    console.log('[Arise] Removing session data from URL');
    url.searchParams.delete('arise_session_data');
}
window.history.replaceState({}, '', url.toString());

let userDiscordAccessToken: string | null = null;
let discordSDK: DiscordSDK | null = null;
let discordReadyPromise: Promise<void> | null = null;
let discordReadyStartedAt = 0;
let discordHookMountSeq = 0;

try {
    // We need to instatiate this here on pageload so that the arise frontend does not change the query params in the URL that the discord sdk uses
    discordSDK = new DiscordSDK(CLIENT_ID, { disableAutoHandshake: true });
} catch (error) {
    if (IS_DISCORD_ACTIVITY) {
        console.error('[Arise] Discord SDK not instatiated', error);
    }
}
let firstLayoutUpdateReceived = false;

// Module-level auth state — persists across hook remounts so a second mount
// picks up the already-resolved values immediately via useState initialisers.
let moduleUser: DiscordUser | null = null;
let moduleAuthData: AuthData | null = null;
let moduleAuthLoading = false;
let moduleAuthError = '';
let moduleAuthDenied = false;
// Single init promise — guarantees SDK auth runs only once per page load.
let discordInitPromise: Promise<boolean | void> | null = null;

interface DiscordUser {
    username: string;
    discriminator: string;
    id: string;
    public_flags: number;
    avatar?: string | null | undefined;
    global_name?: string | null | undefined;
}

interface AuthData {
    access_token: string;
}

export function getQuestId() {
    return QUEST_ID;
}

export default function useDiscord() {
    const discord = useRef<DiscordSDK | null>();
    const [user, _setUser] = useState<DiscordUser | null>(moduleUser);
    const [authLoading, _setAuthLoading] = useState(moduleAuthLoading);
    const [authError, _setAuthError] = useState(moduleAuthError);
    const [authDenied, _setAuthDenied] = useState(moduleAuthDenied);
    const theme = useTheme();

    const dispatch = useAppDispatch();

    // not sure if we need this
    const [authData, _setAuthData] = useState<AuthData | null | undefined>(moduleAuthData);

    // Wrapper setters keep module-level mirrors in sync so remounted instances
    // initialise their useState from the already-resolved values above.
    function setUser(val: DiscordUser | null) {
        moduleUser = val;
        _setUser(val);
    }
    function setAuthLoading(val: boolean) {
        moduleAuthLoading = val;
        _setAuthLoading(val);
    }
    function setAuthError(val: string) {
        moduleAuthError = val;
        _setAuthError(val);
    }
    function setAuthDenied(val: boolean) {
        moduleAuthDenied = val;
        _setAuthDenied(val);
    }
    function setAuthData(val: AuthData | null | undefined) {
        moduleAuthData = val ?? null;
        _setAuthData(val);
    }

    const ariseRegister = useAriseRegister();
    const quest = useDiscordQuest();
    const { setLang } = useLang();

    // Several Discord accounts can sign in through the same browser instance and they all share the
    // same local storage. Cached profiles and titles are namespaced by user id so each account keeps
    // its own, but the Arise auth token is not - it belongs to the previous user, so drop it and let
    // the register flow mint a new one. Also clear the previous user's profile out of redux so their
    // name and progress aren't on screen while this user's profile loads.
    function handleDiscordUserIdentity(discordUserId?: string) {
        const changed = syncDiscordUserIdentity(discordUserId || '');
        if (changed) {
            console.log('[Arise] Discord user changed, dropping the previous user session');
            arise.clearAuthToken();
            dispatch(resetProfileState());
        }
        return changed;
    }

    useEffect(() => {
        const mountId = ++discordHookMountSeq;

        console.log(`[Arise] useDiscord mounted (#${mountId})`);

        // Always point the ref at the singleton SDK instance.
        discord.current = discordSDK;
        // @ts-ignore
        window.discordSDK = discord.current;

        if (!discordInitPromise) {
            // First mount — kick off auth. Capture authorize / authoriseFromSession
            // from this closure; they already use the module-aware setters above.
            discordInitPromise = (async () => {
                try {
                    if (!CLIENT_ID) {
                        console.error('[Arise] No PUBLIC_DISCORD_CLIENT_ID set');
                        return;
                    }

                    let ok = false;

                    if (!ARISE_SESSION_ID) {
                        await ensureDiscordSdkReady(discord.current);
                        // authorise immediately
                        ok = await authorize();
                    } else {
                        console.log('[Arise] Forcing Discord SDK to be ready');
                        discord.current['isReady'] = true;
                        await ensureDiscordSdkReady(discord.current);
                        authoriseFromSession();
                    }
                    if (ok) sendEventV2Once('discord_authenticated');

                    await questCheckUsingSDK();

                    console.log('[Arise] Discord authorized');
                } catch (error) {
                    // Allow a retry on the next mount if init failed.
                    discordInitPromise = null;
                    console.log('[Arise] Could not get discord sdk ready', error);
                }
            })();
        } else {
            console.log(`[Arise] useDiscord already initialised (#${mountId}), reusing state`);
        }

        return () => {
            console.log(`[Arise] useDiscord unmounted (#${mountId})`);
        };
    }, []);

    async function questCheckUsingSDK() {
        try {
            const res = await discord.current?.commands.getQuest();
            console.log('[Arise] quest data from sdk:', JSON.stringify(res));
            sendEventV2('discord-quest-id', [res?.quest_id || 'n/a', 'sdk', CLIENT_ID]);
            if (res) {
                QUEST_ID = res.quest_id;
                dispatch(setQuestFromSDK(res));
            }
        } catch (error) {
            console.error('[Arise] could not get quest info from sdk');
            quest.check();
        }
    }

    async function setLanguageFromDiscordLocale() {
        try {
            const { locale } = await discord.current.commands.userSettingsGetLocale();
            DISCORD_LOCALE_FOR_ANALYTICS = locale;
            sendEventV2Once('discord_locale', [locale]);
            dispatch(setDiscordLocale(locale));
            const savedUserPreference = getSavedLangIso();
            if (!savedUserPreference) {
                // Do not overwrite manually selected language
                setLang(locale);
            }
        } catch (error) {
            console.warn('[Arise] Could not set discord locale:', error);
        }
    }

    async function authoriseFromSession() {
        console.log('[Arise] Authorizing from session');

        if (!ARISE_SESSION_DATA) {
            console.error('[Arise] No session data found in URL');
            return;
        }
        const data = JSON.parse(ARISE_SESSION_DATA);
        console.log('[Arise] Session data from URL', data);
        if (ARISE_SESSION_DATA) {
            if (data.auth && data.authData) {
                // Must run before anything reads the cached profile
                handleDiscordUserIdentity(data.auth.user?.id);
                setUser(data.auth.user);
                setLanguageFromDiscordLocale();
                dispatch(setProfileV2DisplayName(data.auth.user.global_name || data.auth.user.username));
                userDiscordAccessToken = data.authData.access_token;
                ariseDiscord.setAuthToken(data.authData.token);
                setAuthData(data.authData);
                dispatch(setDiscordUser(data.authData.user));

                console.log('[Arise] Restored Discord session from localStorage');

                // This path normally reuses the Arise token already in local storage. If there isn't
                // one (first load, or it belonged to a different Discord user) we have to mint one,
                // otherwise every API call would go out as whoever signed in before.
                if (!arise.getAuthToken()) {
                    console.log('[Arise] No Arise token for this Discord user, registering');
                    ariseRegisterFlow(data.authData.user);
                }
            } else {
                console.error('[Arise] invalid session data in localStorage', ARISE_SESSION_DATA);
            }
        } else {
            console.error('[Arise] No session data found in localStorage');
        }
    }

    async function authorize() {
        if (authLoading) {
            return;
        }
        setAuthLoading(true);
        setAuthDenied(false);
        setAuthError('');

        let discordUser: any = null;

        const scope: OAuthScopes[] = ['identify'];
        const additionalScopes = process.env.PUBLIC_DISCORD_ADDITIONAL_SCOPES;
        if (additionalScopes) {
            additionalScopes.split(',').forEach((d) => scope.push(d as OAuthScopes));
            console.log('[Arise] added additional scopes. ', scope);
        }

        try {
            console.log('[Arise] No auth code found in URL, requesting authorization from Discord');
            const authRes = await discord.current.commands.authorize({
                client_id: CLIENT_ID!,
                response_type: 'code',
                state: '',
                prompt: 'none',
                scope,
            });
            const code = authRes.code;

            sendEventV2('discordlogin__prompt_accepted');
            let authData: any;
            try {
                authData = await tryXTimes(async () => {
                    const res = await ariseDiscord.authorize(code);
                    if (!res.access_token) {
                        sendEventV2('discordlogin__authexchange_no_token');
                        throw new Error('No auth data');
                    }
                    return res;
                }, 3);
            } catch (error) {
                console.error('[Arise] Error exchanging auth code for access token', error);
                sendEventV2('discordlogin__authexchange_failed');
                const fallbackDisplayName = theme?.customJSON?.settings?.guestUsernamePrefix ?? 'Guest';
                dispatch(setProfileV2DisplayName(fallbackDisplayName));
                // @ts-ignore
                setUser({ username: fallbackDisplayName });
                setAuthLoading(false);
                return false;
            }

            const auth = await discord.current.commands.authenticate({
                access_token: authData.access_token,
            });

            // Setup listeners
            // note: layout listerners do not work on password protected activities
            setupLayoutListeners();
            // Must run before anything reads the cached profile
            handleDiscordUserIdentity(auth.user?.id);
            setUser(auth.user);
            dispatch(setProfileV2DisplayName(auth.user.global_name || auth.user.username));
            setLanguageFromDiscordLocale();
            userDiscordAccessToken = authData.access_token;
            setAuthLoading(false);
            setAuthData(authData);
            ariseDiscord.setAuthToken(authData.token);
            dispatch(setDiscordUser(authData.user));
            discordUser = authData.user;

            encourageHardwareAcceleration();
        } catch (error) {
            // User denied authorization
            if (error.code === 5000) {
                sendEventV2('discordlogin__prompt_denied');
                setAuthDenied(true);
            } else if (error.code === 4002) {
                // Already authing, ignore
            } else {
                sendEventV2('discordlogin__error', [
                    error instanceof Error ? error.message : typeof error === 'string' ? error : JSON.stringify(error),
                ]);
                console.log('[Arise] Authorizing Error', error);
                setAuthError('Error authorizing with Discord');
            }
        } finally {
            setAuthLoading(false);
        }

        // Register with Arise
        // This is an async call that doesn't return anything so we don't need to await it
        ariseRegisterFlow(discordUser);

        return true;
    }

    async function ariseRegisterFlow(discordUser: any) {
        try {
            await ariseRegister.register();
        } catch (e) {
            console.error('[Arise] Error registering with Arise', e);
        }

        if (discordUser) {
            // Defer discord premium type until we have user_id.
            sendEventDiscordPremiumType(discordUser.premium_type);
        }
    }

    function openExternalLink(url: string) {
        if (discord.current) {
            discord.current.commands.openExternalLink({
                url,
            });
        }
    }

    function onMinimise() {
        console.log('[Arise] Minimise');
        dispatch(setWindowSize('minimized'));
        dispatch(setShouldHideUI(true));
    }

    function onMaximise() {
        console.log('[Arise] Maximise');
        dispatch(setWindowSize('maximized'));
        dispatch(setShouldHideUI(false));

        setTimeout(() => {
            quest.recheckEnrollment();
        }, 1000);
    }

    async function setupLayoutListeners() {
        const onActivityLayoutModeUpdate = (e: any) => {
            console.log('[Arise] Activity layout mode update', e);
            if (!firstLayoutUpdateReceived) {
                firstLayoutUpdateReceived = true;
                return;
            }
            if (e.layout_mode === 1) {
                onMinimise();
            } else if (e.layout_mode === 0) {
                onMaximise();
            } else {
                console.log('[Arise] Unhandled layout mode', e.layout_mode);
                onMaximise();
            }
        };

        async function onQuestStatusUpdate(e: any) {
            console.log('[Arise] Quest status update from sdk', e);
        }

        if (discord.current) {
            try {
                await discord.current.subscribe(Events.ACTIVITY_LAYOUT_MODE_UPDATE, onActivityLayoutModeUpdate);
                await discord.current.subscribe(Events.QUEST_ENROLLMENT_STATUS_UPDATE, onQuestStatusUpdate);
            } catch (error) {
                console.error('[Arise] sdk subscribe error: ', error);
            }
        } else {
            console.error('[Arise] Unable to setup listener');
        }

        return () => {
            if (discord.current) {
                try {
                    discord.current.unsubscribe(Events.ACTIVITY_LAYOUT_MODE_UPDATE, onActivityLayoutModeUpdate);
                    discord.current.unsubscribe(Events.QUEST_ENROLLMENT_STATUS_UPDATE, onQuestStatusUpdate);
                } catch (error) {
                    console.error('[Arise] sdk UNsubscribe error: ', error);
                }
            }
        };
    }

    async function encourageHardwareAcceleration() {
        try {
            const res = await discord.current.commands.encourageHardwareAcceleration();
            sendEventV2('encourage_hardware_acceleration', [res.enabled ? 'enabled' : 'disabled']);
        } catch (error) {
            console.error('[Arise] Error encouraging hardware acceleration', error);
        }
    }

    return {
        authorize,
        openExternalLink,
        user,
        authError,
        ariseRegister,
        authDenied,
        quest,
        authLoading,
    };
}

async function ensureDiscordSdkReady(sdk: DiscordSDK | null | undefined) {
    if (!sdk) {
        throw new Error('Discord SDK not instantiated');
    }

    // Important: multiple mounts can happen (routing, HMR, parent remount). We only want *one*
    // handshake/ready wait for the lifetime of the page.
    if (!discordReadyPromise) {
        discordReadyStartedAt = Date.now();
        console.log('[Arise] Waiting for Discord SDK to be ready');
        discordReadyPromise = (async () => {
            try {
                try {
                    // `handshake()` is not part of the public typings, but it exists at runtime for this SDK.
                    // If the SDK already handshook, calling again should be harmless; if it throws, we still
                    // want to continue waiting for `ready()`.
                    // @ts-ignore
                    sdk.handshake?.();
                } catch (e) {
                    console.warn('[Arise] Discord SDK handshake threw (continuing)', e);
                }

                // Add a soft timeout so we can surface better diagnostics if READY never arrives.
                await Promise.race([
                    sdk.ready(),
                    new Promise<void>((_, reject) =>
                        setTimeout(() => reject(new Error('Timed out waiting for Discord SDK ready()')), 15_000),
                    ),
                ]);
                console.log('[Arise] Discord SDK is ready');
            } catch (e) {
                // Allow a later retry after transient failures.
                discordReadyPromise = null;
                throw e;
            }
        })();
    } else {
        const elapsedMs = Date.now() - discordReadyStartedAt;
        console.log(`[Arise] Reusing existing Discord SDK ready() wait (elapsed ${elapsedMs}ms)`);
    }

    return discordReadyPromise;
}

function useAriseRegister() {
    const dispatch = useAppDispatch();
    const [trying, setTrying] = useState(false);
    const [failed, setFailed] = useState(false);

    async function register() {
        const totalAttempts = 3;
        let attempts = 0;
        let success = false;
        setTrying(true);
        while (!success && attempts < totalAttempts) {
            try {
                const res = await ariseDiscord.register();
                arise.setAuthToken(res.authToken);
                dispatch(updateIsLoggedIn(true));
                try {
                    const payload = getTokenPayload(res.authToken);
                    setUserId(payload.sub);
                } catch (error) {}
                success = true;
                break;
            } catch (error) {
                console.log('[Arise] Error registering with Arise', error);
                attempts++;
            }
        }
        setTrying(false);
        if (!success) {
            setFailed(true);
        }
    }
    return { register, trying, failed };
}

function useDiscordQuest() {
    const dispatch = useAppDispatch();
    const status = useAppSelector((state) => state.discord.questStatus);
    const checked = useAppSelector((state) => state.discord.questStatusChecked);

    const check = useCallback(async () => {
        try {
            let status = 'not_enrolled';
            try {
                const res = await discordSDK?.commands.getQuest();
                console.log('[Arise] quest data from sdk2:', JSON.stringify(res));
                sendEventV2('discord-quest-id', [res?.quest_id || 'n/a', 'sdk2', CLIENT_ID]);

                if (res) {
                    QUEST_ID = res.quest_id;
                    console.log('[Arise] Quest id:', res.quest_id);
                    dispatch(setQuestFromSDK(res));
                    if (res.completed_at) {
                        status = 'completed';
                    } else if (res.enrolled_at) {
                        status = 'enrolled';
                    }
                }
            } catch (error) {
                const data = await ariseDiscord.checkQuest();

                if (data.completed_at) {
                    status = 'completed';
                } else if (data.enrolled_at) {
                    status = 'enrolled';
                }
                dispatch(setQuestStatus(status));
            }

            return {
                status,
                checked: true,
            };
        } catch (error) {
            console.log('[Arise] Error checking quest status', error);
        }
        return { status: 'not_enrolled', checked: false };
    }, []);

    const complete = useCallback(async () => {
        await setQuestCompleted(dispatch);
        dispatch(setQuestStatus('completed'));
    }, []);

    const progress = useCallback(async (val: number) => {
        await setQuestProgress(val, dispatch);
    }, []);

    const recheckEnrollment = useCallback(async () => {
        console.log('[Arise] Rechecking enrollment');
        if (status === 'not_enrolled' || !status) {
            await check();
        }
    }, []);
    return { check, checked, status, complete, recheckEnrollment, progress };
}

export function gotoQuest() {
    if (discordSDK) {
        discordSDK.commands.openExternalLink({
            url: `https://discord.com/quests/${QUEST_ID}`,
        });
    }
}

export function openExternalLinkDiscord(url: string) {
    if (discordSDK && url) {
        discordSDK.commands.openExternalLink({
            url,
        });
    }
}

export function getEnvMaxDiscordQuestProgress() {
    // Some quests are 0-1 and some 0-100
    return Number(process.env.PUBLIC_DISCORD_QUEST_MAX_PROGRESS) || 1;
}

function shouldSendQuestProgress(): boolean {
    try {
        const discordSettings = store.getState().app.theme.metadata?.customJSON?.discord;

        // ISO date string e.g. "2026-02-04T18:30:00Z"
        const stopAtTime = discordSettings?.discordProgressCallsStopAt;
        if (!stopAtTime) return true;

        const stopAtTimeMs = Date.parse(stopAtTime);
        if (Number.isNaN(stopAtTimeMs)) {
            console.log('[Arise] Invalid discordProgressCallsStopAt date', stopAtTime);
            return true;
        }

        return Date.now() < stopAtTimeMs;
    } catch (error) {
        console.log('[Arise] shouldSendQuestProgress error caught', error);
        return true;
    }
}

export async function setQuestProgress(val: number, dispatch: any) {
    try {
        if (!shouldSendQuestProgress()) {
            console.log('[Arise] Not sending Quest progress - disabled by discordSettings.discordProgressCallsStopAt');
            return;
        }
        const maxProgress = getEnvMaxDiscordQuestProgress();
        if (val >= maxProgress) {
            console.log(
                `[Arise] Cannot set quest progress (${val}) - value is equal to or greater than max progress (${maxProgress}). Use quest-completed instead.`,
            );
            return;
        }

        // Make sure to get the quest id
        await getQuestIdFromSDK(dispatch);

        const { wasSuccessful, throttled, message } = await ariseDiscord.questProgress(val, QUEST_ID);
        if (throttled) return;
        const successInfo = wasSuccessful ? 'quest-progress-successful' : 'quest-progress-unsuccessful';
        const strings = [successInfo, ...(message ? [message] : []), getQuestId(), DISCORD_LOCALE_FOR_ANALYTICS];
        sendEventV2('quest_progress', strings, [Date.now() - activityStartTime, val]);
    } catch (error) {
        console.log('[Arise] Error progressing quest', error);
    }
}

export async function setQuestCompleted(dispatch: any) {
    try {
        if (!shouldSendQuestProgress()) {
            console.log('[Arise] Not sending Quest complete - disabled by discordSettings.discordProgressCallsStopAt');
            return;
        }

        // Make sure to get the quest id
        await getQuestIdFromSDK(dispatch);

        const maxProgress = getEnvMaxDiscordQuestProgress();
        const { wasSuccessful, throttled, message } = await ariseDiscord.questProgress(maxProgress, QUEST_ID);
        if (throttled) return;
        const successInfo = wasSuccessful ? 'quest-complete-successful' : 'quest-complete-unsuccessful';
        const strings = [successInfo, ...(message ? [message] : []), getQuestId(), DISCORD_LOCALE_FOR_ANALYTICS];
        sendEventV2('quest_complete', strings, [Date.now() - activityStartTime]);
    } catch (error) {
        console.log('[Arise] Error completing quest', error);
    }
}

export function isDiscordOnMobile() {
    return discordSDK?.platform === 'mobile';
}

export function discordPlatform() {
    return discordSDK?.platform;
}

export async function triggerDiscordShare(text: string = '', disableAutoLink?: boolean, linkId?: string) {
    if (discordSDK) {
        try {
            // Send text with link added to end automatically, unless disableAutoLink is true
            const shouldLinkToQuest = !!QUEST_ID;

            const url = shouldLinkToQuest
                ? `https://discord.com/quests/${QUEST_ID}`
                : `https://discord.com/activities/${process.env.PUBLIC_DISCORD_CLIENT_ID}`;

            const message = disableAutoLink ? text : `${text} ${url}`;

            sendEventV2('share', ['discord_internal', message, linkId]);

            await discordSDK.commands.shareLink({
                message,
                ...(linkId && { link_id: linkId }),
            });
        } catch (error) {
            console.error('[Arise] Share Dialogue Error: ', JSON.stringify(error));
        }
    } else {
        console.error('[Arise] Discord SDK not available');
    }
}

export async function openImageInDiscordBrowser(imageUrl: string, filename: string) {
    const mediaUrl = await uploadImageToDiscordCDN(imageUrl, filename);
    await discordSDK.commands.openExternalLink({ url: mediaUrl });
}

export async function uploadImageToDiscordCDN(imageURL: string, filename: string) {
    const response = await fetch(imageURL);
    const blob = await response.blob();
    const mimeType = blob.type || 'image/png';

    const buf = await blob.arrayBuffer();
    const imageFile = new File([buf], filename, { type: mimeType });
    const body = new FormData();
    body.append('file', imageFile);

    const attachmentResponse = await fetch(`https://discord.com/api/v10/applications/${CLIENT_ID}/attachment`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${userDiscordAccessToken}`,
        },
        body,
    });
    const attachmentJson = await attachmentResponse.json();
    const mediaUrl = attachmentJson.attachment.url;
    return mediaUrl;
}

export async function shareImageInDiscordDesktop(imageUrl: string, filename: string) {
    try {
        const mediaUrl = await uploadImageToDiscordCDN(imageUrl, filename);
        await discordSDK.commands.openShareMomentDialog({
            mediaUrl,
        });
    } catch (error) {
        console.log('[Arise] Could not openShareMomentDialog', error);
    }
}

function tryXTimes(fn: () => Promise<any>, times: number) {
    return new Promise((resolve, reject) => {
        let attempts = 0;
        const tryFn = async () => {
            try {
                const res = await fn();
                resolve(res);
            } catch (error) {
                console.log(`[Arise] Attempt ${attempts + 1} failed`, error);
                if (attempts < times) {
                    attempts++;
                    setTimeout(tryFn, 1000);
                } else {
                    console.error(`[Arise] All ${times} attempts failed`, error);
                    reject(error);
                }
            }
        };
        tryFn();
    });
}

function sendEventDiscordPremiumType(type: number) {
    const map = {
        0: 'None',
        1: 'Nitro Classic',
        2: 'Nitro',
        3: 'Nitro Basic',
    };
    const val = map[type] || type.toString();
    sendEventV2('discord_nitro', [val]);
}

async function getQuestIdFromSDK(dispatch: any) {
    // Make sure to get the quest id
    try {
        if (discordSDK) {
            const res = await discordSDK.commands.getQuest();
            console.log('[Arise] quest data from sdk3:', JSON.stringify(res));
            sendEventV2('discord-quest-id', [res?.quest_id || 'n/a', 'sdk3', CLIENT_ID]);
            if (res && res.quest_id) {
                QUEST_ID = res.quest_id;
                dispatch(setQuestFromSDK(res));
            }
        }
    } catch (e) {
        try {
            sendEventV2('discord-quest-id-error', [JSON.stringify(e)]);
        } catch (e) {}
    }
}
